// Bloom (dual-filter, energy conserving), auto exposure and final AgX tone mapping / grading.

export const bloomDownFrag = /* glsl */ `
uniform sampler2D uSrc;
uniform vec2 uTexel;
uniform float uFirst;
in vec2 vUv;
out vec4 fragColor;
vec3 karis(vec3 c) { return c / (1.0 + max(max(c.r, c.g), c.b) * 0.25); }
void main() {
  vec2 t = uTexel;
  vec3 a = texture(uSrc, vUv + t * vec2(-2, 2)).rgb;
  vec3 b = texture(uSrc, vUv + t * vec2(0, 2)).rgb;
  vec3 c = texture(uSrc, vUv + t * vec2(2, 2)).rgb;
  vec3 d = texture(uSrc, vUv + t * vec2(-2, 0)).rgb;
  vec3 e = texture(uSrc, vUv).rgb;
  vec3 f = texture(uSrc, vUv + t * vec2(2, 0)).rgb;
  vec3 g = texture(uSrc, vUv + t * vec2(-2, -2)).rgb;
  vec3 h = texture(uSrc, vUv + t * vec2(0, -2)).rgb;
  vec3 i = texture(uSrc, vUv + t * vec2(2, -2)).rgb;
  vec3 j = texture(uSrc, vUv + t * vec2(-1, 1)).rgb;
  vec3 k = texture(uSrc, vUv + t * vec2(1, 1)).rgb;
  vec3 l = texture(uSrc, vUv + t * vec2(-1, -1)).rgb;
  vec3 m = texture(uSrc, vUv + t * vec2(1, -1)).rgb;
  vec3 r;
  if (uFirst > 0.5) {
    r = karis((a + b + d + e) * 0.25) * 0.125 + karis((b + c + e + f) * 0.25) * 0.125
      + karis((d + e + g + h) * 0.25) * 0.125 + karis((e + f + h + i) * 0.25) * 0.125
      + karis((j + k + l + m) * 0.25) * 0.5;
  } else {
    r = e * 0.125 + (a + c + g + i) * 0.03125 + (b + d + f + h) * 0.0625 + (j + k + l + m) * 0.125;
  }
  fragColor = vec4(min(r, vec3(60000.0)), 1.0);
}
`;

export const bloomUpFrag = /* glsl */ `
uniform sampler2D uSrc;
uniform vec2 uTexel;
uniform float uRadius;
in vec2 vUv;
out vec4 fragColor;
void main() {
  vec2 t = uTexel * uRadius;
  vec3 s = texture(uSrc, vUv + vec2(-t.x, t.y)).rgb + texture(uSrc, vUv + vec2(0.0, t.y)).rgb * 2.0 + texture(uSrc, vUv + t).rgb
    + texture(uSrc, vUv + vec2(-t.x, 0.0)).rgb * 2.0 + texture(uSrc, vUv).rgb * 4.0 + texture(uSrc, vUv + vec2(t.x, 0.0)).rgb * 2.0
    + texture(uSrc, vUv - t).rgb + texture(uSrc, vUv + vec2(0.0, -t.y)).rgb * 2.0 + texture(uSrc, vUv + vec2(t.x, -t.y)).rgb;
  fragColor = vec4(s / 16.0, 1.0);
}
`;

// Computes target exposure from a small mip and adapts toward it over time.
export const exposureFrag = /* glsl */ `
uniform sampler2D uSrc;      // small HDR mip
uniform sampler2D uPrev;     // previous exposure (1x1), r = log2 exposure
uniform vec2 uSrcSize;
uniform float uDt;
uniform float uBias;         // user EV compensation
uniform float uReset;
out vec4 fragColor;
void main() {
  float acc = 0.0, wsum = 0.0;
  ivec2 sz = ivec2(uSrcSize);
  for (int y = 0; y < 32; y++) {
    if (y >= sz.y) break;
    for (int x = 0; x < 48; x++) {
      if (x >= sz.x) break;
      vec2 uv = (vec2(x, y) + 0.5) / uSrcSize;
      vec2 cc = uv - 0.5;
      float w = exp(-dot(cc, cc) * 4.0); // center weighted
      float l = lum3(texelFetch(uSrc, ivec2(x, y), 0).rgb);
      acc += log2(max(l, 1e-7)) * w;
      wsum += w;
    }
  }
  float avgLog = acc / wsum;
  // Key: brighter scenes get a slightly higher key; dark scenes are kept darker than mid-grey (night stays night)
  float avgL = exp2(avgLog);
  float key = 0.16;
  float target = log2(key) - avgLog;
  // compress exposure for dark scenes so night stays night
  float T0 = log2(key / 0.007);
  if (target > T0) target = T0 + (target - T0) * 0.45;
  target = clamp(target, -6.0, 15.0) + uBias;
  float prev = texelFetch(uPrev, ivec2(0, 0), 0).r;
  float speed = target > prev ? 1.2 : 2.2; // eyes adapt faster to bright
  float ev = uReset > 0.5 ? target : mix(prev, target, 1.0 - exp(-uDt * speed));
  fragColor = vec4(ev, avgL, 0.0, 1.0);
  }
`;

export const finalFrag = /* glsl */ `
uniform sampler2D uHDR;
uniform sampler2D uBloom;
uniform sampler2D uExposure;
uniform float uBloomStrength;
uniform float uTime;
uniform vec2 uResolution;
uniform float uSaturation;
uniform float uContrast;
uniform float uVignette;
uniform float uGrain;
uniform int uTonemap;
in vec2 vUv;
out vec4 fragColor;

// AgX (Troy Sobotka), minimal fit by bwrensch
const mat3 agxIn = mat3(0.842479062253094, 0.0423282422610123, 0.0423756549057051,
                        0.0784335999999992, 0.878468636469772, 0.0784336,
                        0.0792237451477643, 0.0791661274605434, 0.879142973793104);
const mat3 agxOut = mat3(1.19687900512017, -0.0528968517574562, -0.0529716355144438,
                         -0.0980208811401368, 1.15190312990417, -0.0980434501171241,
                         -0.0990297440797205, -0.0989611768448433, 1.15107367264116);
vec3 agxContrast(vec3 x) {
  vec3 x2 = x * x;
  vec3 x4 = x2 * x2;
  return 15.5 * x4 * x2 - 40.14 * x4 * x + 31.96 * x4 - 6.868 * x2 * x + 0.4298 * x2 + 0.1191 * x - 0.00232;
}
vec3 agx(vec3 c) {
  const float minEv = -12.47393, maxEv = 4.026069;
  c = agxIn * c;
  c = clamp(log2(max(c, vec3(1e-10))), minEv, maxEv);
  c = (c - minEv) / (maxEv - minEv);
  return agxContrast(c);
}
vec3 agxEotf(vec3 c) {
  c = agxOut * c;
  return pow(max(c, vec3(0.0)), vec3(2.2));
}
vec3 agxLook(vec3 c) {
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = pow(max(c, vec3(0.0)), vec3(uContrast));
  return l + uSaturation * (c - l);
}
vec3 linearToSRGB(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}

void main() {
  vec3 hdr = texture(uHDR, vUv).rgb;
  vec3 bloom = texture(uBloom, vUv).rgb;
  vec2 ex = texelFetch(uExposure, ivec2(0, 0), 0).rg;
  float exposure = exp2(ex.r);
  vec3 c = mix(hdr, bloom * (1.0 / 6.0), uBloomStrength) * exposure;
  // scotopic (night) vision: desaturate and shift blue when scene is very dark
  float sceneL = ex.g;
  float scot = smoothstep(0.004, 0.0004, sceneL);
  float lum = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = mix(c, lum * vec3(0.78, 0.86, 1.12), scot * 0.7);
  if (uTonemap == 1) {
    // ACES fitted (Hill)
    const mat3 aIn = mat3(0.59719, 0.07600, 0.02840, 0.35458, 0.90834, 0.13383, 0.04823, 0.01566, 0.83777);
    const mat3 aOut = mat3(1.60475, -0.10208, -0.00327, -0.53108, 1.10813, -0.07276, -0.07367, -0.00605, 1.07602);
    vec3 v = aIn * c;
    vec3 a = v * (v + 0.0245786) - 0.000090537;
    vec3 b = v * (0.983729 * v + 0.4329510) + 0.238081;
    c = clamp(aOut * (a / b), 0.0, 1.0);
    float l2 = dot(c, vec3(0.2126, 0.7152, 0.0722));
    c = max(vec3(0.0), l2 + (uSaturation * 0.92) * (c - l2));
  } else {
    c = agx(c);
    c = agxLook(c);
    c = agxEotf(c);
  }
  // vignette
  vec2 q = vUv - 0.5;
  q.x *= uResolution.x / uResolution.y;
  c *= 1.0 - uVignette * smoothstep(0.35, 1.05, length(q));
  vec3 outc = linearToSRGB(clamp(c, 0.0, 1.0));
  // film grain (in display space, luminance weighted)
  float g = hash12(gl_FragCoord.xy + fract(uTime * 13.7) * 1000.0) - 0.5;
  outc += g * uGrain * (0.6 + 0.4 * scot);
  fragColor = vec4(outc, 1.0);
}
`;
